import { useEffect, useMemo, useState } from 'react';
import type {
  CancellationRule,
  DepositRule,
  NoShowRule,
  PolicyRule,
  Service,
} from '@salonomy/types';
import { Button, Modal, Pill, Toggle } from '@salonomy/ui';
import {
  useDeleteServicePolicyOverride,
  useServicePolicyOverride,
  useSetServicePolicyOverride,
} from '../../data/policy.js';
import { useToast } from '../../lib/toast.js';
import { CancellationRuleFields, DepositRuleFields, NoShowRuleFields } from './PolicyRuleFields.js';
import { PolicyTextPreview } from './PolicyTextPreview.js';

/**
 * Per-service policy override (WS7 phase 2). Each of the three aspects (deposit / cancellation /
 * no-show) is independently either "use the salon default" or "override for this service". An
 * unticked aspect persists as null, which the API folds back to the salon rule — so a salon can say
 * "this £20 fringe trim takes no deposit" without restating the whole policy. `armAutoCharge` is a
 * salon-wide switch and is NOT editable here.
 *
 * The live preview shows the EFFECTIVE text (override folded over the salon default), i.e. exactly
 * what a client booking this specific service will see.
 */
export function ServicePolicyOverrideModal({
  service,
  salonRule,
  onClose,
}: {
  service: Service;
  salonRule: PolicyRule;
  onClose: () => void;
}) {
  const toast = useToast();
  const { data: existing, isLoading } = useServicePolicyOverride(service.id);
  const save = useSetServicePolicyOverride(service.id);
  const remove = useDeleteServicePolicyOverride(service.id);
  const [busy, setBusy] = useState(false);

  // Which aspects this service overrides. We hold the editor back behind `seeded` until the existing
  // override has loaded, so the form doesn't flash "all default" before the real values arrive.
  const [seeded, setSeeded] = useState(false);
  const [depositOn, setDepositOn] = useState(false);
  const [cancelOn, setCancelOn] = useState(false);
  const [noShowOn, setNoShowOn] = useState(false);

  // Working copies of each overridden rule, seeded from the existing override or the salon default.
  const [deposit, setDeposit] = useState<DepositRule>(salonRule.deposit);
  const [cancellation, setCancellation] = useState<CancellationRule>(salonRule.cancellation);
  const [noShow, setNoShow] = useState<NoShowRule>(salonRule.noShow);

  // Seed local state from the loaded override exactly once (when the query first settles).
  useEffect(() => {
    if (seeded || isLoading) return;
    setDepositOn(Boolean(existing?.deposit));
    setCancelOn(Boolean(existing?.cancellation));
    setNoShowOn(Boolean(existing?.noShow));
    setDeposit(existing?.deposit ?? salonRule.deposit);
    setCancellation(existing?.cancellation ?? salonRule.cancellation);
    setNoShow(existing?.noShow ?? salonRule.noShow);
    setSeeded(true);
  }, [seeded, isLoading, existing, salonRule]);

  // The EFFECTIVE rule a client of THIS service sees: an overridden aspect uses the local copy,
  // an un-overridden aspect falls back to the salon default. armAutoCharge tracks the salon switch.
  const effective = useMemo<PolicyRule>(
    () => ({
      deposit: depositOn ? deposit : salonRule.deposit,
      cancellation: cancelOn ? cancellation : salonRule.cancellation,
      noShow: noShowOn ? noShow : salonRule.noShow,
      armAutoCharge: salonRule.armAutoCharge,
    }),
    [depositOn, cancelOn, noShowOn, deposit, cancellation, noShow, salonRule],
  );

  const anyOverride = Boolean(depositOn || cancelOn || noShowOn);
  const hasExisting = Boolean(existing);

  async function submit() {
    setBusy(true);
    try {
      if (!anyOverride) {
        // Nothing overridden — if a row existed, removing it reverts cleanly to the salon default.
        if (hasExisting) await remove.mutateAsync();
        toast.success(`${service.name} now uses the salon policy`);
      } else {
        await save.mutateAsync({
          deposit: depositOn ? deposit : null,
          cancellation: cancelOn ? cancellation : null,
          noShow: noShowOn ? noShow : null,
        });
        toast.success(`Policy for ${service.name} saved`);
      }
      onClose();
    } catch {
      toast.error('Could not save this service policy.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Policy — ${service.name}`}
      width={560}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || isLoading}>
            Save
          </Button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Tick an aspect to set it for this service. Anything you leave un-ticked follows the salon
        policy.
        {hasExisting ? (
          <Pill tone="cobalt" className="mono">
            {' '}
            Has override
          </Pill>
        ) : null}
      </div>

      {seeded ? (
        <>
          <OverrideAspect label="Deposit" on={depositOn} onToggle={setDepositOn}>
            <DepositRuleFields rule={deposit} onChange={setDeposit} />
          </OverrideAspect>

          <OverrideAspect label="Cancellation window" on={cancelOn} onToggle={setCancelOn}>
            <CancellationRuleFields rule={cancellation} onChange={setCancellation} />
          </OverrideAspect>

          <OverrideAspect label="No-show fee" on={noShowOn} onToggle={setNoShowOn}>
            <NoShowRuleFields rule={noShow} onChange={setNoShow} />
          </OverrideAspect>

          <div style={{ marginTop: 14 }}>
            <PolicyTextPreview rule={effective} />
          </div>
        </>
      ) : (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0' }}>
          Loading…
        </div>
      )}
    </Modal>
  );
}

/** One togglable aspect: a switch that, when on, reveals the rule editor for that aspect. */
function OverrideAspect({
  label,
  on,
  onToggle,
  children,
}: {
  label: string;
  on: boolean;
  onToggle: (next: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--slm-border)',
        borderRadius: 10,
        padding: '10px 12px',
        marginBottom: 10,
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5 }}>
        <Toggle checked={on} onChange={onToggle} label={`Override ${label}`} labelHidden />
        <span style={{ fontWeight: 600 }}>Override {label.toLowerCase()}</span>
        {!on ? (
          <span className="muted" style={{ fontSize: 12 }}>
            using salon policy
          </span>
        ) : null}
      </label>
      {on ? <div style={{ marginTop: 8 }}>{children}</div> : null}
    </div>
  );
}
